import { z } from 'zod'

import ExternalTravelerIcon from '@/assets/external-traveler.svg'
import { CountryNameZ, CurrencyZ, POSCountryNameZ, RelativeURLZ } from '@/types/common'
import { CountriesCode } from '@/types/locale'
import { UserNewZ } from '@/types/userNew'

export const UserLightZ = UserNewZ.pick({
	id: true,
	first_name: true,
	middle_name: true,
	last_name: true,
	image_url: true,
	email: true,
	language_code: true,
	currency_code: true,
	invoice_profile: true,
	flight_search_mock_enabled: true,
	deleted: true,
	gender: true,
})

export const ExpenseTypeZ = z.enum(['standard', 'mileage', 'daily_allowance'])
export type ExpenseType = z.infer<typeof ExpenseTypeZ>

export const ExpenseSubmitterZ = UserLightZ
export type ExpenseSubmitter = z.infer<typeof ExpenseSubmitterZ>

export const CustomAttendeeResponseZ = UserNewZ.pick({ id: true, first_name: true, last_name: true }).extend({
	is_internal: z.literal(false),
	company_name: z.string(),
})
export type CustomAttendeeResponse = z.infer<typeof CustomAttendeeResponseZ>

const CustomAttendeeZ = CustomAttendeeResponseZ.extend({ image_url: UserNewZ.shape.image_url })

export const CustomAttendeeLocalZ = CustomAttendeeZ.transform((attendee) => ({
	...attendee,
	image_url: ExternalTravelerIcon,
}))

export const CustomAttendeeRequestZ = CustomAttendeeZ.omit({ id: true }).extend({
	id: CustomAttendeeResponseZ.shape.id.optional(),
})
export const ExpenseStatusZ = z.enum(['open', 'submitted', 'approved', 'rejected', 'reimbursed'])
export type ExpenseStatus = z.infer<typeof ExpenseStatusZ>

const ExpenseReportBasicZ = z.object({
	id: z.union([z.number(), z.string()]),
	name: z.string(),
	amount: z.number().nullish(),
})
export type ExpenseReportBasic = z.infer<typeof ExpenseReportBasicZ>

export const ExpenseResponseAttendeeZ = z.union([
	UserLightZ.extend({ is_internal: z.literal(true) }),
	CustomAttendeeLocalZ,
])
export type ExpenseResponseAttendee = z.infer<typeof ExpenseResponseAttendeeZ>

export const ExpenseRequestAttendeeZ = z.union([UserNewZ.pick({ id: true }), CustomAttendeeRequestZ])

const ReimbursableZ = z.object({ reimbursable: z.boolean() })

export const ExpenseFileZ = z.object({
	name: z.string(),
	original_file_url: RelativeURLZ,
	original_size: z.number().positive(),
	preview_file_url: RelativeURLZ,
	thumbnail_file_url: RelativeURLZ,
})
export type ExpenseFile = z.infer<typeof ExpenseFileZ>

export const UnitsOfLengthZ = z.enum(['km', 'mi'])
export type UnitsOfLength = z.infer<typeof UnitsOfLengthZ>

export const MerchantCountryZ = z.object({ name: CountryNameZ, code: z.enum(CountriesCode) })
export type MerchantCountry = z.infer<typeof MerchantCountryZ>

export const IsTravelRelatedZ = z.union([z.literal('0'), z.literal('1')]).optional()
export type IsTravelRelated = z.infer<typeof IsTravelRelatedZ>

const ExpenseFileResponseZ = z.object({
	original_file_url: RelativeURLZ,
	preview_file_url: RelativeURLZ,
	thumbnail_file_url: RelativeURLZ,
})
export type ExpenseFileResponse = z.infer<typeof ExpenseFileResponseZ>

const TaxAndVatRateZ = z.object({
	id: z.number(),
	country: z.object({ name: POSCountryNameZ, code: z.enum(CountriesCode) }),
	merchant_country: MerchantCountryZ.extend({ code: z.union([MerchantCountryZ.shape.code, z.literal('')]) }),
	all_countries: z.boolean(),
	rate: z.number().min(0).max(100),
	name: z.string().max(255),
})
export type TaxAndVatRate = z.infer<typeof TaxAndVatRateZ>

export const TaxAndVatRateApiZ = TaxAndVatRateZ.pick({
	id: true,
	all_countries: true,
	rate: true,
	name: true,
}).extend({
	country: POSCountryNameZ,
	country_code: z.enum(CountriesCode),
	merchant_country_name: CountryNameZ,
	merchant_country_code: z.enum(CountriesCode).nullish(),
})
export type TaxAndVatRateApi = z.infer<typeof TaxAndVatRateApiZ>

export const TaxInExpenseZ = TaxAndVatRateApiZ.pick({ id: true, rate: true, name: true })

export const ExpenseModelZ = z.object({
	id: z.number().brand<'ExpenseId'>(),
	createdAt: z.date(),
	currency: CurrencyZ,
	description: z.string().max(1000).nullish(),
	isTravelRelated: IsTravelRelatedZ,
	report: ExpenseReportBasicZ.optional(),
	status: ExpenseStatusZ,
	submitter: ExpenseSubmitterZ,
	type: ExpenseTypeZ,
	...ReimbursableZ.shape,
})
export type ExpenseModel = z.infer<typeof ExpenseModelZ>

const ReportResponseZ = z.object({
	expense_report: z.string().nullish(),
	report: ExpenseReportBasicZ.optional(),
	report_amount: z.number().nullish(),
	report_id: z.number().nullish(),
})

export const ExpenseResponseZ = z.object({
	...ExpenseModelZ.pick({
		id: true,
		currency: true,
		description: true,
		reimbursable: true,
		submitter: true,
	}).shape,
	created_dt: z.string().datetime({ offset: true }),
	expense_status: ExpenseStatusZ,
	expense_type: ExpenseTypeZ,
	is_travel_related: z.boolean().nullish(),
	...ReportResponseZ.shape,
})

export const ExpenseRequestZ = z.object({
	...ExpenseResponseZ.omit({ id: true, created_dt: true, currency: true, submitter: true }).shape,
	submitter_id: z.number(),
})
export type ExpenseRequest = z.infer<typeof ExpenseRequestZ>
